import React, { useEffect, useState } from 'react';
import { Alert, Table, Spinner, Badge } from 'react-bootstrap';
import { fetchLivreurDisponibilite } from './DataService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import './LivreurDisponibilite.css';

const LivreurDisponibilite = ({ dateLivraison, selectedLivreurId, onSelectLivreur }) => {
  const [disponibilite, setDisponibilite] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (dateLivraison) {
      loadDisponibilite();
    }
  }, [dateLivraison]);

  const loadDisponibilite = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchLivreurDisponibilite(dateLivraison);

      // Trier par disponibilité : disponibles d'abord, puis occupés, puis surchargés
      const sorted = data.sort((a, b) => {
        const statusOrder = { 'available': 0, 'busy': 1, 'overloaded': 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      });

      setDisponibilite(sorted);
    } catch (err) {
      console.error('Erreur lors du chargement de la disponibilité:', err);
      setError('Impossible de charger la disponibilité des livreurs');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'available':
        return { icon: '🟢', label: 'Disponible', variant: 'success' };
      case 'busy':
        return { icon: '🟡', label: 'Occupé', variant: 'warning' };
      case 'overloaded':
        return { icon: '🔴', label: 'Surchargé', variant: 'danger' };
      case 'unavailable':
        return { icon: '⚫', label: 'Indisponible (congé/repos)', variant: 'dark' };
      default:
        return { icon: '⚪', label: 'Inconnu', variant: 'secondary' };
    }
  };

  const formatProchaineDispo = (nextAvailable) => {
    if (!nextAvailable) return 'Maintenant';

    const now = new Date();
    const next = new Date(nextAvailable);
    const diffMs = next - now;

    if (diffMs <= 0) return 'Maintenant';

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const remainingMinutes = diffMinutes % 60;

    const timeStr = format(next, 'HH:mm', { locale: fr });

    if (diffHours === 0) {
      return `${timeStr} (dans ${remainingMinutes}min)`;
    } else if (diffHours < 24) {
      return `${timeStr} (dans ${diffHours}h${remainingMinutes > 0 ? remainingMinutes : ''})`;
    } else {
      return format(next, 'dd/MM à HH:mm', { locale: fr });
    }
  };

  const handleRowClick = (livreur) => {
    if (onSelectLivreur) {
      onSelectLivreur(livreur.id);
    }
  };

  // Vérifier si le livreur sélectionné est surchargé et suggérer une alternative
  const selectedLivreur = disponibilite.find(l => l.id === selectedLivreurId);
  const suggestion = selectedLivreur?.status === 'overloaded'
    ? disponibilite.find(l => l.status === 'available')
    : null;

  if (!dateLivraison) {
    return null;
  }

  if (loading) {
    return (
      <div className="livreur-disponibilite-loading text-center py-3">
        <Spinner animation="border" size="sm" /> Chargement de la disponibilité...
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="mt-3">
        {error}
      </Alert>
    );
  }

  if (disponibilite.length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        Aucun livreur disponible à ce créneau. Essayez de décaler l'heure.
      </Alert>
    );
  }

  return (
    <div className="livreur-disponibilite mt-3">
      {/* Suggestion si livreur surchargé */}
      {suggestion && (
        <Alert variant="warning" className="d-flex justify-content-between align-items-center">
          <div>
            <strong>⚠️ Attention :</strong> {selectedLivreur.name} est surchargé à ce créneau ({selectedLivreur.deliveries_count} livraisons)
            <br />
            <strong>💡 Suggestion :</strong> {suggestion.name} est disponible
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => handleRowClick(suggestion)}
          >
            Changer pour {suggestion.name.split(' ')[0]}
          </button>
        </Alert>
      )}

      <div className="disponibilite-header">
        <strong>📊 Disponibilité des livreurs</strong>
        {dateLivraison && (
          <span className="text-muted ms-2">
            le {format(new Date(dateLivraison), 'dd/MM/yyyy à HH:mm', { locale: fr })}
          </span>
        )}
      </div>

      <Table hover size="sm" className="disponibilite-table mb-0">
        <thead>
          <tr>
            <th>Livreur</th>
            <th>Statut</th>
            <th className="text-center">Livraisons</th>
            <th>Prochaine dispo</th>
          </tr>
        </thead>
        <tbody>
          {disponibilite.map((livreur) => {
            const statusInfo = getStatusInfo(livreur.status);
            const isSelected = livreur.id === selectedLivreurId;

            return (
              <tr
                key={livreur.id}
                onClick={() => handleRowClick(livreur)}
                className={`disponibilite-row ${isSelected ? 'selected' : ''} ${livreur.status}`}
              >
                <td>
                  {statusInfo.icon} <strong>{livreur.name}</strong>
                  {isSelected && <Badge bg="primary" className="ms-2">Sélectionné</Badge>}
                </td>
                <td>
                  <Badge bg={statusInfo.variant}>
                    {statusInfo.label}
                  </Badge>
                </td>
                <td className="text-center">
                  <strong>{livreur.deliveries_count}</strong>
                </td>
                <td className="text-muted">
                  {formatProchaineDispo(livreur.next_available)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="disponibilite-footer text-muted small">
        <span>💡 Cliquez sur une ligne pour sélectionner ce livreur</span>
      </div>
    </div>
  );
};

export default LivreurDisponibilite;
